import DataGrid, { Column, Item, Toolbar, type DataGridRef } from 'devextreme-react/data-grid';
import SelectBox from 'devextreme-react/select-box';
import { useMemo, useRef } from 'react';
import type { ChartThemeColors } from '../../charts/useChartThemeColors';
import { OPS_SITE_GRID_PROPS, OpsGridToolbarItems, OpsSiteGridChrome } from '../../opsDataGrid';
import { formatActivityDateTime } from '../formatters';
import { NamedBreakdownBarChart } from '../userActivityCharts';
import type { UserActivityAnalyticsUsersSection, UserActivityUserRow } from '../types';
import type { UserActivityUsersMetric } from '../userActivityPrefs';
import { ReportChartCard } from './ReportChartCard';
import { drilldownForUser } from './sessionDrilldown';

const METRIC_OPTIONS: { value: UserActivityUsersMetric; text: string }[] = [
  { value: 'sessions', text: 'Sessions' },
  { value: 'uniqueUsers', text: 'Unique users' },
  { value: 'totalMinutes', text: 'Total minutes' },
  { value: 'activeDays', text: 'Active days' }
];

function metricValue(row: UserActivityUserRow, metric: UserActivityUsersMetric): number {
  if (metric === 'totalMinutes') return row.totalMinutes ?? 0;
  if (metric === 'activeDays') return row.activeDays;
  if (metric === 'uniqueUsers') return 1;
  return row.sessions;
}

export function UsersReport({
  users,
  chartColors,
  metric,
  onMetricChange,
  showTenantColumn,
  onDrilldown
}: {
  users: UserActivityAnalyticsUsersSection;
  chartColors: ChartThemeColors;
  metric: UserActivityUsersMetric;
  onMetricChange: (next: UserActivityUsersMetric) => void;
  showTenantColumn: boolean;
  onDrilldown: (label: string, filter: unknown) => void;
}) {
  const gridRef = useRef<DataGridRef>(null);
  const chartRows = useMemo(
    () =>
      users.users.slice(0, 20).map((u) => ({
        key: u.key,
        label: u.label,
        sessions: metricValue(u, metric),
        uniqueUsers: 1,
        totalMinutes: u.totalMinutes
      })),
    [users.users, metric]
  );

  return (
    <div className="user-activity-analytics__body user-activity-analytics__body--scroll">
      <ReportChartCard
        title="Top users"
        subtitle="Ranked by the selected metric. Double-click a grid row to open matching sessions."
        empty={users.users.length === 0}
        toolbar={
          <SelectBox
            dataSource={METRIC_OPTIONS}
            value={metric}
            valueExpr="value"
            displayExpr="text"
            width={160}
            onValueChanged={(e) => onMetricChange(e.value as UserActivityUsersMetric)}
          />
        }
      >
        <div className="user-activity-analytics__chart-host" style={{ minHeight: 320 }}>
          <NamedBreakdownBarChart
            data={chartRows}
            colors={chartColors}
            valueField="sessions"
            fileName="user-activity-users"
          />
        </div>
      </ReportChartCard>

      <section className="user-activity-report-card" style={{ marginTop: 12 }}>
        <div className="user-activity-report-card__header">
          <h3 className="ops-section-title" style={{ margin: 0 }}>
            Users in range
          </h3>
        </div>
        <div className="user-activity-analytics__active-grid" style={{ height: 360 }}>
          <DataGrid
            ref={gridRef}
            dataSource={users.users}
            keyExpr="key"
            height="100%"
            showBorders
            columnAutoWidth
            {...OPS_SITE_GRID_PROPS}
            onRowDblClick={(e) => {
              const row = e.data as UserActivityUserRow;
              const drill = drilldownForUser(row.key, row.label);
              onDrilldown(drill.label, drill.filter);
            }}
          >
            <OpsSiteGridChrome
              stateStorageKey="user-activity-users-grid"
              searchPlaceholder="Filter users…"
              showPaging={users.users.length > 50}
            />
            <Toolbar>
              <OpsGridToolbarItems
                gridRef={gridRef}
                onRefresh={() => gridRef.current?.instance()?.refresh()}
                export={{
                  fileName: 'user-activity-users',
                  worksheetName: 'Users',
                  hint: 'Export users'
                }}
              />
              <Item name="searchPanel" location="after" />
            </Toolbar>
            <Column dataField="label" caption="User" minWidth={180} />
            <Column dataField="roles" caption="Roles" minWidth={140} />
            {showTenantColumn ? (
              <Column dataField="tenantId" caption="Tenant" minWidth={160} />
            ) : null}
            <Column dataField="sessions" caption="Sessions" width={100} />
            <Column dataField="activeDays" caption="Active days" width={110} />
            <Column dataField="totalMinutes" caption="Total minutes" width={120} />
            <Column dataField="averageMinutes" caption="Avg minutes" width={110} />
            <Column
              dataField="firstSeenAt"
              caption="First seen"
              width={170}
              calculateDisplayValue={(row: UserActivityUserRow) =>
                formatActivityDateTime(row.firstSeenAt)}
            />
            <Column
              dataField="lastSeenAt"
              caption="Last seen"
              width={170}
              calculateDisplayValue={(row: UserActivityUserRow) =>
                formatActivityDateTime(row.lastSeenAt)}
            />
            <Column
              caption=""
              width={110}
              allowSorting={false}
              cellRender={(c) => {
                const row = c.data as UserActivityUserRow;
                return (
                  <button
                    type="button"
                    className="user-activity-drilldown-link"
                    onClick={() => {
                      const drill = drilldownForUser(row.key, row.label);
                      onDrilldown(drill.label, drill.filter);
                    }}
                  >
                    View sessions
                  </button>
                );
              }}
            />
          </DataGrid>
        </div>
      </section>
    </div>
  );
}
